import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { adminApi } from '../../services/api';
import {
  Euro, UserPlus, AlertTriangle,
  Loader2, Activity, Gift, Handshake, Briefcase, Clock, CheckCircle2, Building,
} from 'lucide-react';

const PIE_COLORS = ['#D44E20', '#8B5CF6', '#10B981', '#F59E0B', '#3B82F6', '#EC4899'];


function StatCard({ icon: Icon, label, value, sub, color = '#D44E20', testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
          <Icon size={17} style={{ color }} />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-[#1A1A1A]">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}


function BillingTypeTile({ icon: Icon, label, count, desc, color, testId }) {
  return (
    <div className="rounded-lg p-3 border border-slate-100" data-testid={testId} style={{ backgroundColor: `${color}08` }}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} style={{ color }} />
        <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{label}</p>
      </div>
      <p className="text-xl font-bold text-[#1A1A1A] tabular-nums">{count}</p>
      <p className="text-[10px] text-slate-500">{desc}</p>
    </div>
  );
}


export default function SuperadminCockpit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/superadmin/billing/cockpit')
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-10 flex justify-center mb-6">
        <Loader2 size={28} className="animate-spin text-[#D44E20]" />
      </div>
    );
  }
  if (!data) return null;

  const growthPositive = data.mrr_growth_pct !== null && data.mrr_growth_pct >= 0;

  return (
    <div className="mb-6" data-testid="cockpit-section">
      <div className="flex items-center gap-2 mb-4">
        <Activity size={18} className="text-[#D44E20]" />
        <h2 className="text-lg font-bold text-[#1A1A1A]">Cockpit — Crescita & Salute</h2>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard testId="cockpit-mrr" icon={Euro} label="MRR reale (solo paying)" color="#D44E20"
          value={`${Number(data.current_mrr || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
          sub={data.mrr_growth_pct !== null ? `${growthPositive ? '+' : ''}${data.mrr_growth_pct}% vs mese scorso` : 'Nessun confronto'} />
        <StatCard testId="cockpit-total-brands" icon={Building} label="Brand totali attivi" color="#3B82F6"
          value={data.total_active_brands || 0}
          sub={`${data.paying_brands_count || 0} paying · ${(data.total_active_brands || 0) - (data.paying_brands_count || 0)} non-paying`} />
        <StatCard testId="cockpit-churn" icon={AlertTriangle} label="Churn mese (paying)" color="#EF4444"
          value={`${data.churn_rate_pct || 0}%`}
          sub={`${data.churn_this_month || 0} brand scaduti`} />
        <StatCard testId="cockpit-new" icon={UserPlus} label="Nuove attivazioni" color="#10B981"
          value={data.new_this_month_count || 0}
          sub="Questo mese" />
      </div>

      {/* Billing type breakdown */}
      {data.billing_type_breakdown && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5" data-testid="cockpit-billing-breakdown">
          <div className="flex items-center gap-2 mb-3">
            <Building size={14} className="text-slate-500" />
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Distribuzione brand per tipo abbonamento</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <BillingTypeTile testId="bt-paid" icon={CheckCircle2} label="Paying" color="#10B981"
              count={data.billing_type_breakdown.paid || 0} desc="In MRR" />
            <BillingTypeTile testId="bt-trial" icon={Clock} label="Trial" color="#F59E0B"
              count={data.billing_type_breakdown.trial || 0} desc="Con scadenza" />
            <BillingTypeTile testId="bt-complimentary" icon={Gift} label="Cortesia" color="#EC4899"
              count={data.billing_type_breakdown.complimentary || 0} desc="Regalo / founder" />
            <BillingTypeTile testId="bt-partner" icon={Handshake} label="Partner" color="#3B82F6"
              count={data.billing_type_breakdown.partner || 0} desc="Collaborazioni" />
            <BillingTypeTile testId="bt-internal" icon={Briefcase} label="Interno" color="#8B5CF6"
              count={data.billing_type_breakdown.internal || 0} desc="Lazo / test" />
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        {/* MRR timeseries */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5" data-testid="cockpit-mrr-chart">
          <h3 className="text-sm font-bold text-[#1A1A1A] mb-1">MRR ultimi 12 mesi</h3>
          <p className="text-xs text-slate-500 mb-4">Andamento del ricavo ricorrente mensile.</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.mrr_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }}
                formatter={(v) => [`${Number(v).toFixed(2)} €`, 'MRR']}
              />
              <Line type="monotone" dataKey="mrr" stroke="#D44E20" strokeWidth={2.5}
                    dot={{ r: 3, fill: '#D44E20' }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Plan distribution */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="cockpit-plan-distribution">
          <h3 className="text-sm font-bold text-[#1A1A1A] mb-1">Distribuzione piani</h3>
          <p className="text-xs text-slate-500 mb-4">Brand attivi per piano.</p>
          {data.plan_distribution.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-10">Nessun piano attivo</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data.plan_distribution} dataKey="count" nameKey="name"
                       cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {data.plan_distribution.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-1">
                {data.plan_distribution.map((p, i) => (
                  <div key={p.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-slate-600">{p.name}</span>
                    </span>
                    <span className="font-semibold text-[#1A1A1A]">{p.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lists row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* At-risk brands */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="cockpit-at-risk">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={15} className="text-amber-500" />
            <h3 className="text-sm font-bold text-[#1A1A1A]">Brand a rischio</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">Abbonamenti in scadenza entro 14 giorni.</p>
          {data.at_risk.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-6">Nessun brand a rischio 🎉</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.at_risk.map(b => (
                <div key={b.brand_id} className="flex items-center justify-between p-2.5 rounded-lg bg-amber-50 border border-amber-100">
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{b.brand_name}</p>
                    <p className="text-[11px] text-slate-500">{b.plan_name} · scade {new Date(b.expires_at).toLocaleDateString('it-IT')}</p>
                  </div>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                    {b.days_left === 0 ? 'Oggi' : `${b.days_left}g`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* New activations */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="cockpit-new-activations">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus size={15} className="text-emerald-600" />
            <h3 className="text-sm font-bold text-[#1A1A1A]">Nuove attivazioni del mese</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">Brand che hanno attivato un piano questo mese.</p>
          {data.new_this_month.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-6">Nessuna nuova attivazione</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.new_this_month.map((b, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-50 border border-emerald-100">
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{b.brand_name}</p>
                    <p className="text-[11px] text-slate-500">
                      {b.plan_name} · {new Date(b.activated_at).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
